import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import AppButton from "../../AppButton";
import AppModal from "../../AppModal";
import AppModalActions from "../../AppModal/AppModalActions";
import AppModalContent from "../../AppModal/AppModalContent";
import AppModalHeader from "../../AppModal/AppModalHeader";
import AppTagInput from "../../AppTagInput";
import { fetcher } from "../../../services/api";
import AppSelect from "../../AppSelect";
import TrashIcon from "../../../assets/icons/trash";
import ChevronDownIcon from "../../../assets/icons/chveron-down";
import { useIsMobile } from "../../../hooks/useIsMobile";
import "./ManageContentModal.css";

interface ManageContentModalProps {
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  data?: any;
  onSave?: (updatedGroup: any) => void;
}

export default function ManageContentModal({
  isVisible,
  setIsVisible,
  data,
  onSave,
}: ManageContentModalProps) {
  const [contentFilterTypes, setContentFilterTypes] = useState<{
    [key: number]: string;
  }>({});
  const [countries, setCountries] = useState<
    { country_code: string; display_name: string }[]
  >([]);
  const [filterLevelMapping, setFilterLevelMapping] = useState<
    Record<string, number[]>
  >({});
  const [selectedFilterLevel, setSelectedFilterLevel] = useState<
    string | undefined
  >();
  const [settings, setSettings] = useState<{
    default_content_filter_type: number[];
    black_listed_words: string[];
    apply_for_others: [];
    black_listed_words_by_country: Record<string, string[]>;
  }>({
    default_content_filter_type: [],
    black_listed_words: [],
    apply_for_others: [],
    black_listed_words_by_country: {},
  });
  const [countryConfigs, setCountryConfigs] = useState<
    { country: string | null; words: string[] }[]
  >([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (data) {
      const entries = Object.entries(data.black_listed_words_by_country || {});
      const configs =
        entries.length > 0
          ? entries.map(([country, words]) => ({
              country,
              words: words as string[],
            }))
          : [{ country: null, words: [] }];

      setCountryConfigs(configs);

      setSettings({
        default_content_filter_type: [],
        black_listed_words: data.blackListedWords || [],
        apply_for_others: [],
        black_listed_words_by_country: data.black_listed_words_by_country || {},
      });

      fetcher<{ data: { country_code: string; display_name: string }[] }>(
        "/publisher/client/countries_list",
        {
          method: "GET",
        }
      )
        .then((res) => {
          if (res?.data?.data) {
            setCountries(res.data.data);
          } else {
            console.error("Invalid response from countries_list:", res);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch countries:", err);
        });
    }
  }, [data]);

  useEffect(() => {
    const fetchContentFilterTypes = async () => {
      try {
        const res = await fetcher<{
          data: { id: number; display_name: string }[];
        }>("/publisher/client/content-filter-types", {
          method: "GET",
        });

        if (res?.data?.data) {
          const filterTypes: { [key: number]: string } = {};
          const nameToIdMap: { [key: string]: number } = {};
          res.data.data.forEach((type) => {
            filterTypes[type.id] = type.display_name;
            nameToIdMap[type.display_name] = type.id;
          });

          const newFilterLevelMapping = {
            high: ["G"],
            medium: ["G", "PG"],
            low: ["G", "PG", "PG-13"],
            off: ["G", "PG", "PG-13", "NR"],
          };

          const mappedFilterLevels: Record<string, number[]> = {};
          Object.keys(newFilterLevelMapping).forEach((key) => {
            mappedFilterLevels[key] = newFilterLevelMapping[
              key as keyof typeof newFilterLevelMapping
            ]
              .map((name) => nameToIdMap[name])
              .filter((id): id is number => typeof id === "number");
          });

          setContentFilterTypes(filterTypes);
          setFilterLevelMapping(mappedFilterLevels);

          if (data?.contentFilterTypes) {
            const defaultIds = data.contentFilterTypes
              .map((name: string) => nameToIdMap[name])
              .filter((id: unknown) => typeof id === "number");

            setSettings((prev) => ({
              ...prev,
              default_content_filter_type: defaultIds,
            }));
          }
        } else {
          console.error("Invalid response for content filter types:", res);
        }
      } catch (error) {
        console.error("Failed to fetch content filter types:", error);
      }
    };

    fetchContentFilterTypes();
  }, [data]);

  const countryOptions = useMemo(
    () =>
      countries.map(({ country_code, display_name }) => ({
        value: country_code,
        name: display_name,
      })),
    [countries]
  );

  const handleManageContent = async (clientKey: string, settings: any) => {
    try {
      const { error } = await fetcher<any>(
        `/publisher/client/${clientKey}/manage-content`,
        {
          method: "POST",
          body: JSON.stringify(settings),
        }
      );

      if (error) {
        throw error;
      }
      const updatedGroup = {
        ...data,
        blackListedWords: settings.black_listed_words,
        blackListedWordsCount: settings.black_listed_words.length,
        black_listed_words_by_country: settings.black_listed_words_by_country,
        contentFilterTypes: settings.default_content_filter_type.map(
          (id: number) => contentFilterTypes[id]
        ),
      };
      onSave?.(updatedGroup);
      setIsVisible(false);
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  const selectedContentFilterLevel = useMemo(() => {
    if (selectedFilterLevel) return selectedFilterLevel;

    if (
      !settings.default_content_filter_type?.length ||
      !Object.keys(filterLevelMapping).length
    )
      return undefined;

    const currentFilter = settings.default_content_filter_type
      .slice()
      .sort()
      .join(",");

    return Object.entries(filterLevelMapping).find(
      ([, ids]) => ids.slice().sort().join(",") === currentFilter
    )?.[0];
  }, [
    settings.default_content_filter_type,
    filterLevelMapping,
    selectedFilterLevel,
  ]);

  return (
    <AppModal
      isVisible={isVisible}
      onChange={() => {
        setIsVisible(!isVisible);
      }}
    >
      <AppModalHeader
        onClose={() => {
          setIsVisible(!isVisible);
        }}
      >
        Manage content for{" "}
        <span className="manage-content-title">{data?.groupName}</span>
      </AppModalHeader>
      <AppModalContent>
        <div className="manage-content">
          <div className="manage-content-item">
            <div className="manage-content-subtitle">Global Settings</div>
            <p className="manage-content-desc">
              Settings configured in the Global Settings apply across the entire
              application.
            </p>
            <div className="manage-content-wrapper">
              <span className="manage-content-label">
                Content Filter Options
              </span>
              <p className="manage-content-desc">
                Review the{" "}
                <a
                  href="https://docs.klipy.com/additional-resources/content-filtering"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Content Filtering doc
                </a>{" "}
                and choose what suits your needs.
              </p>
              <AppSelect
                key={selectedContentFilterLevel}
                options={[
                  { value: "high", name: "High: G" },
                  { value: "medium", name: "Medium: G, PG" },
                  { value: "low", name: "Low: G, PG, PG-13" },
                  { value: "off", name: "Off: G, PG, PG-13, R (no nudity)" },
                ]}
                placeholder="Select filter"
                secondary
                single
                noFlag
                wide
                value={[selectedContentFilterLevel || ""]}
                onSelect={(val) => {
                  const selectedLevel = val[0] as string;
                  setSelectedFilterLevel(selectedLevel);
                  setSettings((prev) => ({
                    ...prev,
                    default_content_filter_type:
                      filterLevelMapping[selectedLevel] || [],
                  }));
                }}
              />
            </div>

            <div className="manage-content-wrapper">
              <span className="manage-content-label">Black listed words</span>
              <div className="manage-content-actions">
                <AppTagInput
                  value={settings.black_listed_words}
                  onChange={(newWords) =>
                    setSettings((prev) => ({
                      ...prev,
                      black_listed_words: newWords.filter(Boolean),
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <div className="manage-content-item">
            <div className="manage-content-subtitle">Country Settings</div>
            <p className="manage-content-desc">
              Settings configured in the Country Settings apply specifically to
              the selected country.
            </p>
            <div className="manage-content-wrapper">
              <div className="manage-content-wrapper-title">
                <p className="manage-content-desc">Country</p>
                <p className="manage-content-desc">Keywords</p>
              </div>
              {countryConfigs.map((config, index) => (
                <div
                  className="manage-content-actions"
                  key={config.country || index}
                >
                  <p className="manage-content-desc mobile-content">Country</p>
                  <div className="manage-content-action">
                    <AppSelect
                      options={countryOptions.filter((i) => {
                        return (
                          i.value === config.country ||
                          countryConfigs.map((j) => j.country).indexOf(i.value) < 0
                        );
                      })}
                      placeholder="Select Country"
                      numerical
                      secondary
                      searchable
                      single
                      value={config.country ? [config.country] : undefined}
                      onSelect={(value) => {
                        const selected = value[0] as string;
                        setCountryConfigs((prev) => {
                          const updated = [...prev];
                          updated[index].country = selected;
                          return updated;
                        });
                      }}
                      formatOptionLabel={(option: any) => {
                        if (isMobile && typeof option.name === "string" && option.name.length >= 10) {
                          return `${option.name.slice(0, 8)}...`;
                        }
                        return option.name;
                      }}
                    />
                  </div>
                  <p className="manage-content-desc mobile-content">Keywords</p>
                  <div className="group-of-actions">
                    <AppTagInput
                    value={config.words}
                    small
                    expanded={index === expandedIndex}
                    onChange={(newWords) => {
                      const updated = [...countryConfigs];
                      updated[index].words = newWords.filter(Boolean);
                      setCountryConfigs(updated);

                      setSettings((prev) => ({
                        ...prev,
                        black_listed_words_by_country: {
                          ...prev.black_listed_words_by_country,
                          [config.country as string]: config.words,
                        },
                      }));

                      setExpandedIndex(index);
                    }}
                    disabled={!config.country}
                  />

                  <div className="manage-content-action">
                    <div
                      className="manage-content-delete-icon"
                      onClick={() => {
                        const newConfigs = [...countryConfigs];
                        const countryToRemove = newConfigs[index].country;
                        newConfigs.splice(index, 1);
                        const finalConfigs =
                          newConfigs.length === 0
                            ? [{ country: null, words: [] }]
                            : newConfigs;

                        setCountryConfigs(finalConfigs);
                        setSettings((prev) => {
                          const updated = { ...prev };
                          if (countryToRemove) {
                            delete updated.black_listed_words_by_country[
                              countryToRemove
                            ];
                          }
                          return updated;
                        });
                      }}
                    >
                      <TrashIcon />
                    </div>

                    <div
                      className={classNames("manage-content-edit-icon", {
                        rotated: expandedIndex === index,
                      })}
                      onClick={() => {
                        setExpandedIndex((prev) =>
                          prev === index ? null : index
                        );
                      }}
                    >
                      <ChevronDownIcon />
                    </div>
                  </div>
                  </div>
                </div>
              ))}

              {data?.black_listed_words_by_country &&
                Object.keys(data?.black_listed_words_by_country).length >=
                  0 && (
                  <AppButton
                    disabled={
                      countryConfigs.length > 0 &&
                      (!countryConfigs[countryConfigs.length - 1].country ||
                        countryConfigs[countryConfigs.length - 1].words
                          .length === 0)
                    }
                    variant="secondary"
                    sm
                    onClick={() =>
                      setCountryConfigs((prev) => [
                        ...prev,
                        { country: null, words: [] },
                      ])
                    }
                  >
                    Add Country
                  </AppButton>
                )}
            </div>
          </div>
        </div>
      </AppModalContent>
      <AppModalActions>
        <AppButton
          variant="secondary"
          fullWidth
          onClick={() => handleManageContent(data?.key, settings)}
        >
          Save
        </AppButton>
      </AppModalActions>
    </AppModal>
  );
}
